import React from 'react';
import {
  Box,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Avatar
} from '@mui/material';
import { FormatQuote, CheckCircleOutline } from '@mui/icons-material';

const WHY_CHOOSE_US = [
  'Manufacturing Top Quality Products',
  'Creating Unique Colours & Designs to bring out each team’s individuality',
  'Maximizing Athlete Performance',
  'Reaching the Global Market',
  'Exceeding Expectations',
  'Creating Innovative Product'
];

const MissionSection = () => (
  <Grid container spacing={6} alignItems="center" sx={{ py: { xs: 6, md: 10 } }}>
    <Grid item xs={12} md={6}>
      <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
        Our Mission
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        “Empowering teams to perform at their best, our mission at Zaror is to craft
        premium bespoke sports clothing that seamlessly blends innovation, style, and
        comfort. With meticulous attention to detail and a commitment to excellence, we
        strive to elevate the athletic experience, helping teams to express their unique
        identities while achieving peak performance.
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Our dedication to quality craftsmanship and personalized service ensures that
        every piece of clothing we create embodies the spirit of individuality, enabling
        athletes to push boundaries, break records, and conquer challenges with
        confidence.”
      </Typography>
    </Grid>
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: 6
        }}
      >
        <img
          src="/assets/home/hero.jpg"
          alt="Zaror bespoke kits"
          style={{ width: '100%', display: 'block' }}
        />
      </Box>
    </Grid>
  </Grid>
);

const WhyChooseUsSection = () => (
  <Grid
    container
    spacing={6}
    alignItems="center"
    sx={{ py: { xs: 6, md: 10 }, flexDirection: { xs: 'column-reverse', md: 'row' } }}
  >
    <Grid item xs={12} md={6}>
      <Box
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: 6
        }}
      >
        <img
          src="/assets/home/overview-action.jpg"
          alt="Team collaboration"
          style={{ width: '100%', display: 'block' }}
        />
      </Box>
    </Grid>
    <Grid item xs={12} md={6}>
      <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
        Why Choose Us
      </Typography>
      <Stack spacing={1.5}>
        {WHY_CHOOSE_US.map((point, index) => (
          <Stack direction="row" spacing={2} alignItems="flex-start" key={index}>
            <CheckCircleOutline color="secondary" sx={{ mt: 0.5 }} />
            <Typography variant="body1" color="text.primary">
              {point}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Grid>
  </Grid>
);

const ManufacturingSection = () => (
  <Box sx={{ py: { xs: 6, md: 10 } }}>
    <Paper
      elevation={3}
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)'
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          Manufacturing Top Quality Products
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Whether you’re a grassroots team, a collegiate powerhouse, or a professional
          club, we understand that every team has its own unique requirements and
          aspirations. That’s why we offer a comprehensive range of customization
          options, from design and colour selection to fabric choice and branding,
          ensuring that your teamwear reflects your identity and vision.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          But we’re not just in the business of making clothing—we’re in the business of
          building relationships and fostering success. Through our commitment to
          exceptional customer service, timely delivery, and unwavering support, we
          strive to be more than just a supplier but a trusted partner in your team’s
          journey to greatness.
        </Typography>
      </Stack>
    </Paper>
  </Box>
);

const About = () => {
  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/assets/home/slide-1.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          py: { xs: 12, md: 14 }
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="h6" sx={{ letterSpacing: 4, textTransform: 'uppercase' }}>
              Company
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                textAlign: { xs: 'center', md: 'left' },
                lineHeight: 1.1
              }}
            >
              Welcome to Zaror.
            </Typography>
            <Divider
              sx={{
                width: 80,
                borderColor: 'secondary.main',
                borderWidth: 2,
                mt: 1
              }}
            />
            <Typography
              variant="h5"
              sx={{
                maxWidth: 720,
                textAlign: { xs: 'center', md: 'left' },
                fontWeight: 400
              }}
            >
              Why settle for off-the-shelf uniforms when you can have custom-made teamwear that
              sets your team apart?
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={4}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ffffff 0%, #f7f7f7 100%)'
            }}
          >
            <FormatQuote sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
              Attitude, confidence, possibility — I want people to wear Zaror and feel those
              feelings.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="center">
              <Avatar
                src="/assets/home/overview-team.png"
                alt="Tommy Rivers"
                sx={{ width: 80, height: 80 }}
              />
              <Stack spacing={0} alignItems={{ xs: 'center', sm: 'flex-start' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Tommy Rivers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Designer
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <MissionSection />
        <WhyChooseUsSection />
        <ManufacturingSection />
      </Container>

      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default About;
